import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type {
    InitPromozavrPayload,
    PromozavrQueueSelection,
    PromozavrQueueSelections,
    PromozavrRendererSource,
    PromozavrState,
    SelectMultiplePromosModelInput,
    SelectMultiplePromosModelResponse,
} from './types';

function createEmptyQueueSelection(): PromozavrQueueSelection {
    return {
        isSelected: false,
    };
}

function createEmptyQueueSelections(): PromozavrQueueSelections {
    return {
        regular: createEmptyQueueSelection(),
        persistent: createEmptyQueueSelection(),
    };
}

function getQueueSelection(response: SelectMultiplePromosModelResponse['regular']): PromozavrQueueSelection {
    return {
        selectedPromo: response.selectedPromos?.[0],
        translations: response.translations,
        images: response.images,
        isSelected: response.isSelected,
    };
}

const initialState: PromozavrState = {
    status: 'idle',
    queueSelections: createEmptyQueueSelections(),
    rendererSource: 'local',
};

export const initPromozavr = createAction<InitPromozavrPayload>('@PROMOZAVR/INIT');

const promozavrSlice = createSlice({
    name: '@PROMOZAVR',
    initialState,
    reducers: {
        resetPromozavrState() {
            return initialState;
        },
        startPromozavrRequest(
            state,
            {
                payload,
            }: PayloadAction<{
                request: SelectMultiplePromosModelInput;
                requestKey: string;
                rendererSource: PromozavrRendererSource;
            }>
        ) {
            state.status = 'loading';
            state.request = payload.request;
            state.requestKey = payload.requestKey;
            state.queueSelections = createEmptyQueueSelections();
            state.rendererSource = payload.rendererSource;
            state.lastError = undefined;
        },
        finishPromozavrRequest(state, { payload }: PayloadAction<SelectMultiplePromosModelResponse>) {
            const regularSelection = getQueueSelection(payload.regular);
            const persistentSelection = getQueueSelection(payload.persistent);

            state.status = 'success';
            state.queueSelections = {
                regular: regularSelection,
                persistent: persistentSelection,
            };
            state.lastError = undefined;
        },
        failPromozavrRequest(state, { payload }: PayloadAction<string>) {
            state.status = 'error';
            state.queueSelections = createEmptyQueueSelections();
            state.lastError = payload;
        },
        setPromozavrRendererSource(state, { payload }: PayloadAction<PromozavrRendererSource>) {
            state.rendererSource = payload;
        },
    },
});

export const {
    failPromozavrRequest,
    finishPromozavrRequest,
    resetPromozavrState,
    setPromozavrRendererSource,
    startPromozavrRequest,
} = promozavrSlice.actions;

export const promozavrReducer = promozavrSlice.reducer;
